import styles from './TemplateSetUp.module.css';
import { cn } from '@/lib/utils';
import {
	Control,
	FieldErrors,
	UseFormRegister,
	UseFormWatch,
} from 'react-hook-form';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { TemplateLibrary } from '@/lib/types/TemplateBuilder/templateTypes';
import {
	capitalizeFirstLetter,
	wordCountValidation,
} from '@/lib/functions/TemplateBuilder/templateUtils';
import TemplateSetupIcon from '@/components/ui/icons/new/TemplateSetupIcon';
import { Multiselect } from '@/components/ui/shared/Multiselect/Multiselect';
import { Textarea } from '@/components/ui/textarea';
import PrimaryButton from '@/components/ui/shared/Button/PrimaryButton/PrimaryButton';
import SecondaryButton from '@/components/ui/shared/Button/SecondaryButton/SecondaryButton';
import { Input } from '@/components/ui/input';
import {
	FormControl,
	FormField,
	FormItem,
	FormMessage,
} from '@/components/ui/form';
import { useAppSelector } from '@/lib/hooks/hooks';
import { RootState } from '@/redux/store/store';

interface TemplateSetupInterface {
	register: UseFormRegister<TemplateLibrary>;
	errors: FieldErrors<TemplateLibrary>;
	control: Control<TemplateLibrary, any>;
	watch: UseFormWatch<TemplateLibrary>;
}
const TemplateSetUp: FC<TemplateSetupInterface> = ({ control }) => {
	const templateSetupVisible = useAppSelector(
		(root: RootState) => root.templates.isTemplateSetupVisible
	);
	const isTemplateEditable = useAppSelector(
		(root: RootState) => root.templates.isTemplateEditable
	);
	const templateBuilderData = useAppSelector(
		(root: RootState) => root.templates.templateBuilder
	);
	const templateTagOptions = useAppSelector(
		(root: RootState) => root.templates.templateTagOptions
	);
	const intermediatePage = useAppSelector(
		(root: RootState) => root.page.intermediatePage
	);
	const navigate = useNavigate();
	const [tags, setTags] = useState<any>([]);

	useEffect(() => {
		if (templateBuilderData && templateSetupVisible) {
			const cTags: any = [];
			if (templateBuilderData.tags && templateBuilderData.tags.length > 0) {
				templateBuilderData.tags.forEach((value: string | []) => {
					// const newTagId = uuid();
					if (value) {
						if (typeof value === 'string') {
							cTags.push({
								organizationIdentifier: undefined,
								value,
								label: capitalizeFirstLetter(value),
							});
						} else {
							cTags.push(value);
						}
					}
				});

				setTags(cTags);
			}
		}
	}, [templateBuilderData, templateSetupVisible]);
	const isLoading = useAppSelector(
		(root: RootState) => root.templates.isLoading
	);

	const cancelChanges = () => {
		if (!intermediatePage) {
			navigate('/template');
		} else {
			intermediatePage.onCancel &&
				intermediatePage.onCancel(intermediatePage.state);
		}
	};

	return (
		<div
			className={cn(
				styles.setUpCard,
				`flex flex-col gap-5 justify-center ${templateSetupVisible ? 'visible' : 'hidden'}`
			)}
		>
			<div className="grid grid-cols-12 gap-7 items-center">
				<div className="col-span-3 items-center flex justify-end">
					<TemplateSetupIcon />
				</div>
				<div className="col-span-9">
					<div className="flex flex-row mb-2">
						<h2 className="dynamic-xl-large font-bold text-secondary">
							{isTemplateEditable
								? 'Edit A Template'
								: 'Setting up a New Template'}
						</h2>
					</div>
					<div className="flex flex-row">
						<div className="flex flex-col w-1/2 gap-2">
							<h2 className="dynamic-medium font-bold text-black">
								Template Name
							</h2>
							<span className="dynamic-small text-black">
								Give your template a unique name.
							</span>
						</div>
					</div>
					<div className="flex flex-col w-full mt-2">
						<FormField
							control={control}
							name="name"
							rules={{
								required: 'Template Name is required',
								maxLength: {
									value: 100,
									message: 'Name should be under 100 characters.',
								},
							}}
							render={({ field }) => {
								return (
									<FormItem>
										<FormControl>
											<Input
												{...field}
												onChange={field.onChange}
												placeholder="Give your template a name"
												className={`p-4 rounded-xl bg-white h-12 dynamic-text border`}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								);
							}}
						/>
					</div>
				</div>
			</div>
			<div className="flex flex-col justify-center gap-2">
				<h2 className="dynamic-medium font-bold text-black">Tags</h2>
				<span className="dynamic-small text-black">
					Please add tags that will make it easier for your team to find this
					template.
				</span>
				<div className="w-[920px]">
					<FormField
						control={control}
						name="tags"
						rules={{
							required: 'Tag is required',
						}}
						render={({ field }) => {
							return (
								<FormItem>
									<FormControl>
										<Multiselect
											title="Enter tags"
											options={templateTagOptions || []}
											value={tags}
											canAddOption={true}
											onSelectedValuesChange={(values: any) => {
												setTags(values);
												field.onChange(values);
											}}
										></Multiselect>
									</FormControl>
									<FormMessage />
								</FormItem>
							);
						}}
					/>
				</div>
			</div>
			<div className="flex flex-col justify-center gap-2">
				<h2 className="dynamic-medium font-bold text-black">Description</h2>
				<span className="dynamic-small text-black">
					Describe the purpose of this template so that your team knows how to
					use it.
				</span>
				<FormField
					control={control}
					name="description"
					rules={{
						required: 'Description is required',
						validate: (value) => wordCountValidation(value, 500),
					}}
					render={({ field }) => {
						return (
							<FormItem className="col-span-12">
								<FormControl>
									<Textarea
										{...field}
										onChange={field.onChange}
										placeholder="Enter Description"
										className={`p-4 rounded-2xl dynamic-text hidden-scroll resize-none border-[#d3dde2] bg-white focus-visible:ring-0 focus-visible:shadow-none focus-visible:ring-offset-0 min-h-[150px] lg:min-h-[120px]`}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						);
					}}
				/>
			</div>
			<div className="flex flex-row justify-end mt-[5px] gap-4 bg-white">
				<SecondaryButton type="button" onClick={cancelChanges}>
					Cancel
				</SecondaryButton>
				{isTemplateEditable ? (
					<PrimaryButton type="submit" disabled={isLoading}>
						Edit A template
					</PrimaryButton>
				) : (
					<PrimaryButton type="submit" disabled={isLoading}>
						Start building template
					</PrimaryButton>
				)}
			</div>
		</div>
	);
};

export default TemplateSetUp;
